import { useCallback } from 'react';
import { odata } from '@bingads-webui/http-util';
import { useOdataOptions } from './use-odata-options';
import type { AigcRequestActivity } from './use-generate-business-snapshot';

const API_NAME = 'odataapi/campaign/v2/accountsv2/getbusinesssnapshotresult';

/**
 * Hook that polls GET GetBusinessSnapshotResult to check AI analysis status
 * as part of the Agentic Onboarding wizard flow.
 *
 * Route shape matches the MT controller (`AccountsV2ControllerTests` /
 * `GetBusinessSnapshotResult_*`):
 *   AccountsV2/GetBusinessSnapshotResult(CustomerId={cid},AccountId={aid},TaskId='{taskId}')
 *
 * Optionally accepts an activity logger for request/response/error tracing
 * keyed off the response's x-ms-requestid header. Note this hook is called
 * once per poll tick; pass `activity` only on the polls you want logged
 * (e.g. terminal status transitions) to avoid spamming.
 *
 * @returns {Function} getBusinessSnapshotResult - accepts taskId, returns poll response
 */
export const useGetBusinessSnapshotResult = () => {
  const [odataOptions, { cid, aid }] = useOdataOptions('Advertiser/V2');

  return useCallback((taskId: string, activity?: AigcRequestActivity) => {
    const url = `AccountsV2/GetBusinessSnapshotResult(CustomerId=${cid},AccountId=${aid},TaskId='${taskId}')`;

    // Log request + response/error from a single jQuery `complete` callback
    // so all three trace lines carry the same x-ms-requestid (extracted
    // from the response headers).
    return odata.get({
      ...odataOptions,
      url,
      complete: (jqXHR: any, textStatus: string) => {
        if (!activity) return;
        const requestId =
          (jqXHR && typeof jqXHR.getResponseHeader === 'function')
            ? (jqXHR.getResponseHeader('x-ms-requestid') ?? undefined)
            : undefined;
        const isSuccess = textStatus === 'success'
          || textStatus === 'notmodified'
          || textStatus === 'nocontent';

        activity.trace(
          `[PMaxLiteSignup][AgenticOnboarding] GetBusinessSnapshotResult request: taskId=${taskId}`,
          API_NAME,
          requestId
        );

        if (isSuccess) {
          const responseBody = jqXHR?.responseJSON;
          const status = responseBody?.Data?.Status ?? '';
          const nextAction = responseBody?.Data?.NextAction ?? '';

          activity.trace(
            `[PMaxLiteSignup][AgenticOnboarding] GetBusinessSnapshotResult response: taskId=${taskId} status=${status} nextAction=${nextAction}`,
            API_NAME,
            requestId
          );
        } else {
          const errorBody = jqXHR?.responseJSON ?? jqXHR?.responseText ?? jqXHR?.statusText ?? textStatus;
          activity.error(
            `[PMaxLiteSignup][AgenticOnboarding] GetBusinessSnapshotResult failed: taskId=${taskId} httpStatus=${jqXHR?.status} textStatus=${textStatus} ${JSON.stringify(errorBody)}`,
            API_NAME,
            requestId
          );
        }
      },
    } as any);
  }, [odataOptions, cid, aid]);
};
